import { useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppStore } from '../stores/appStore';
import { useUIStore } from '../stores/uiStore';
import { Building2, Shield } from 'lucide-react';

export function DashboardScreen() {
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const { organizations, fetchOrganizations, loading } = useAppStore();

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Track if data has been loaded to prevent refetching
  const hasFetchedRef = useRef(false);

  // Memoize the fetch functions to prevent infinite loops
  const fetchDashboardData = useCallback(async () => {
    // Prevent multiple fetches
    if (hasFetchedRef.current) return;

    try {
      hasFetchedRef.current = true;

      if (isSuperAdmin()) {
        await fetchOrganizations();
      }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
    }
  }, [isSuperAdmin, fetchOrganizations]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="space-y-3 md:space-y-4 animate-fade-in">
      {/* Welcome Header with Gradient - Mobile Optimized */}
      <div className="modern-card-gradient overflow-hidden">
        <div className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl p-2 shadow-lg animate-bounce-subtle flex-shrink-0">
              <img src="/logo.png" alt="Logo" className="h-8 w-auto object-contain" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold gradient-text mb-0.5 truncate">
                Welcome back, {user?.name || 'User'}
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base font-medium truncate">
                {isSuperAdmin() ? 'Manage your authentication infrastructure' :
                 isAdmin() ? 'Manage your organization\'s applications' :
                 'Manage your profile settings'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/50 dark:bg-gray-800/50 rounded-full border border-primary-200 dark:border-gray-700 flex-shrink-0">
            <span className="text-xs font-medium text-primary-600 dark:text-gray-200 whitespace-nowrap">
              {user?.role === 'super_admin' ? '👑 Super Admin' : user?.role === 'admin' ? '🛡️ Org Admin' : '👤 User'}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards - Mobile Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {isSuperAdmin() && (
          <div className="modern-card group overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-primary-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="h-5 w-5 text-primary-600" />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary-600">
                    {loading ? <div className="skeleton w-6 h-6 rounded"></div> : organizations.length}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">Organizations</div>
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-full"></div>
            </div>
          </div>
        )}

        <div className="modern-card group overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-secondary-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-5 w-5 text-secondary-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-secondary-600 capitalize">
                  {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Org Admin' : 'User'}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">Your Role</div>
              </div>
            </div>
            <div className="h-1 bg-gradient-to-r from-secondary-400 to-accent-400 rounded-full"></div>
          </div>
        </div>

        <div className="modern-card group overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-accent-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <svg className="h-5 w-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-accent-600">
                  {isSuperAdmin() ? 'Full' : isAdmin() ? 'Org' : 'Basic'}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">Access Level</div>
              </div>
            </div>
            <div className="h-1 bg-gradient-to-r from-accent-400 to-primary-400 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Quick Actions Grid - Mobile Stacked */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {isSuperAdmin() && (
          <div className="modern-card-gradient p-4 md:p-5 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg text-white shadow-lg flex-shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Organization Management</h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">Create and manage organizations.</p>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/dashboard/orgs" className="btn-primary btn-ripple px-3 py-1.5 text-xs">Manage</Link>
                  <Link to="/dashboard/orgs/create" className="btn-outline px-3 py-1.5 text-xs">Create</Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {isAdmin() && (
          <div className="modern-card-gradient p-4 md:p-5 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-gradient-to-br from-secondary-500 to-accent-500 rounded-lg text-white shadow-lg flex-shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Application Management</h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">Configure OAuth2 apps and secrets.</p>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/dashboard/apps" className="btn-secondary btn-ripple px-3 py-1.5 text-xs">Manage</Link>
                  <Link to="/dashboard/apps/create" className="btn-outline px-3 py-1.5 text-xs">Create</Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {isSuperAdmin() && (
          <div className="modern-card-gradient p-4 md:p-5 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-gradient-to-br from-accent-500 to-primary-500 rounded-lg text-white shadow-lg flex-shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">User Management</h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">Manage user accounts and roles.</p>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/dashboard/users" className="btn-primary btn-ripple px-3 py-1.5 text-xs">Manage</Link>
                  <Link to="/dashboard/users/create" className="btn-outline px-3 py-1.5 text-xs">Create</Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {isSuperAdmin() && (
          <div className="modern-card-gradient p-4 md:p-5 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-gradient-to-br from-success-500 to-warning-500 rounded-lg text-white shadow-lg flex-shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Audit Logs</h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">Monitor system activity.</p>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/dashboard/audit" className="btn-success btn-ripple px-3 py-1.5 text-xs">View Logs</Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Account Information Card - Mobile Optimized */}
      <div className="modern-card-gradient overflow-hidden">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">Account Information</h2>
            <Link to="/dashboard/profile" className="btn-outline btn-ripple px-3 py-1.5 text-xs md:text-sm">
              Edit Profile
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
            <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-neutral-200 dark:border-gray-700 hover:border-primary-300 transition-colors">
              <span className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">Name</span>
              <span className="font-bold text-gray-900 dark:text-gray-100 text-sm truncate max-w-[150px] md:max-w-none">{user?.name || '-'}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-neutral-200 dark:border-gray-700 hover:border-primary-300 transition-colors">
              <span className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">Email</span>
              <span className="font-bold text-gray-900 dark:text-gray-100 text-sm truncate max-w-[150px] md:max-w-none">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-neutral-200 dark:border-gray-700 hover:border-primary-300 transition-colors">
              <span className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">Verified</span>
              <span className={`font-bold text-sm ${user?.email_verified ? 'text-success-600' : 'text-danger-600'}`}>
                {user?.email_verified ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-neutral-200 dark:border-gray-700 hover:border-primary-300 transition-colors">
              <span className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">Created</span>
              <span className="font-bold text-gray-900 dark:text-gray-100 text-sm truncate max-w-[120px]">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
              </span>
            </div>
            {user?.org_id && (
              <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-neutral-200 dark:border-gray-700 hover:border-primary-300 transition-colors md:col-span-2">
                <span className="text-neutral-600 dark:text-neutral-400 text-sm font-medium">Organization ID</span>
                <code className="text-[10px] md:text-xs font-mono bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-1 rounded truncate max-w-[150px] md:max-w-none">
                  {user.org_id}
                </code>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
